import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { LabelGroup } from "@dropbox/dig-components/label_group";
import { Layer } from "@dropbox/dig-components/layer";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine, UpsellPictogram } from "@dropbox/dig-icons/assets";
import { Link } from "@mirage/link/Link";
import { useIsAdminUser } from "@mirage/service-auth/useDropboxAccount";
import { useDashTrialInfo } from "@mirage/service-dash-trial/hooks/useDashTrialInfo";
import i18n from "@mirage/translations";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { trialEndingNotificationAtom } from "./atoms";
import styles from "./TrialEndingNotification.module.css";
import { TrialActionType } from "./types";
import { getActionSurface, useLogSelectUpsellComponent } from "./utils";

const getNotificationText = (actionType: TrialActionType): string => {
  switch (actionType) {
    case TrialActionType.Search:
      return i18n.t("trial_ending_notification_search");
    case TrialActionType.Chat:
      return i18n.t("trial_ending_notification_chat");
    case TrialActionType.Stacks:
      return i18n.t("trial_ending_notification_stacks");
    case TrialActionType.Connector:
      return i18n.t("trial_ending_notification_connector");
  }
};

const UpsellIcon = () => {
  return (
    <div className={styles.iconWrapper}>
      <div className={styles.iconContainer}>
        <UIIcon src={UpsellPictogram} size="small" />
      </div>
    </div>
  );
};

export const TrialEndingNotification = () => {
  const { dashTrialActivated, daysRemainingInDashTrial } = useDashTrialInfo();
  const isAdmin = useIsAdminUser();
  const [notificationState, setNotificationState] = useAtom(
    trialEndingNotificationAtom,
  );
  const { isVisible, daysLeft, actionType, containerRef } = notificationState;
  const notificationText = getNotificationText(actionType);
  const logSelectUpsell = useLogSelectUpsellComponent();
  const href = "https://dash.dropbox.com/plans";

  const handleClose = () => {
    setNotificationState((prev) => ({ ...prev, isVisible: false }));
  };

  const handleViewPlans = () => {
    handleClose();
    logSelectUpsell({
      actionSurface: getActionSurface(actionType),
      ctaName: isAdmin ? "view_plans" : "ask_your_admin",
    });
  };

  useEffect(() => {
    setNotificationState((prev) => ({
      ...prev,
      daysLeft: daysRemainingInDashTrial,
    }));
  }, [daysRemainingInDashTrial]);

  if (!dashTrialActivated || !isVisible) {
    return null;
  }

  const notificationContent = (
    <Box
      borderRadius="Large"
      padding={{
        small: "Micro Large",
      }}
      backgroundColor={"Background Base"}
      borderStyle="Solid"
      borderColor={"Border Subtle"}
      boxShadow="Raised"
      display="flex"
      color="Text Base"
      position={containerRef ? "static" : "fixed"}
      pointerEvents="auto"
      bottom={containerRef ? undefined : "0"}
      marginBottom={containerRef ? undefined : "Macro Small"}
      placeItems="center"
      style={{
        maxWidth: "850px",
      }}
    >
      <Split gap="16" alignY="center">
        <Split.Item>
          <UpsellIcon />
        </Split.Item>
        <Split.Item>
          <LabelGroup
            withLabel={notificationText}
            withSubText={
              // using withSubText to reduce gap between label and subtext
              <Text className={styles.bannerText}>
                {isAdmin
                  ? i18n.t("trial_ending_days_remaining_subtitle", {
                      count: daysLeft,
                    })
                  : i18n.t("trial_ending_days_remaining_member_subtitle", {
                      count: daysLeft,
                    })}
              </Text>
            }
          ></LabelGroup>
        </Split.Item>
        {isAdmin && (
          <Split.Item>
            <Link
              variant="monochromatic"
              hasNoUnderline
              href={href}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="primary" onClick={handleViewPlans}>
                {i18n.t("trial_ending_view_plans")}
              </Button>
            </Link>
          </Split.Item>
        )}
        <Split.Item>
          <IconButton
            size="medium"
            variant="transparent"
            onClick={handleClose}
            aria-label="close"
            data-testid="close-notification"
          >
            <UIIcon src={CloseLine} />
          </IconButton>
        </Split.Item>
      </Split>
    </Box>
  );

  // If containerRef is provided, render inside that container using Portal
  if (containerRef?.current) {
    return createPortal(notificationContent, containerRef.current);
  }

  // Otherwise render with fixed positioning and Layer
  return (
    <Layer>
      <div className={styles.container}>{notificationContent}</div>
    </Layer>
  );
};
